import styled from '@emotion/styled';
import { Info, Warning } from '@mui/icons-material';
import { Box, FormControlLabel, Radio, RadioGroup, Tooltip, Typography } from '@mui/material';
import SituacaoAcordaoBusiness from '@/dominio/SituacaoAcordaoBusiness';
import SituacaoJulgamentoProcessoBusiness from '@/dominio/SituacaoJulgamentoProcessoBusiness';
import { computed, makeObservable, runInAction } from 'mobx';
import { inject, observer } from 'mobx-react';
import Configuracao from '@/util/Configuracao';
import ReactComponent from '../../../ReactComponent';

const KeyframesBlinkPublicacaoObrigatoriaInconsistente = styled('span')({
    '@keyframes blink': {
        from: {
            opacity: 1
        },
        to: {
            opacity: 0.3
        }
    },
    animation: 'blink 1s',
    animationIterationCount: 'infinite'
});

class PublicacaoAcordao extends ReactComponent {
    constructor(props) {
        super(props);

        makeObservable(this, {
            registro: computed,
            isProcessoApreciado: computed,
            isProcessoJulgado: computed,
            marcacaoManualPublicacaoAcordao: computed,
            marcacaoAutomaticaPublicacaoAcordao: computed,
            tooltipMarcacaoPublicacaoAcordao: computed,
            acordaoObrigatorio: computed
        });
    }

    get registro() {
        return this.observableProps.registro;
    }

    get isProcessoApreciado() {
        return SituacaoJulgamentoProcessoBusiness.isProcessoApreciado(this.registro?.resultadoJulgamento);
    }

    get isProcessoJulgado() {
        return SituacaoJulgamentoProcessoBusiness.isProcessoJulgado(this.registro?.resultadoJulgamento);
    }

    get marcacaoManualPublicacaoAcordao() {
        return (
            this.registro?.informacoesAdicionais?.marcadorAcordaoObrigatorio === true ||
            this.registro?.informacoesAdicionais?.marcadorAcordaoObrigatorio === false
        );
    }

    get marcacaoAutomaticaPublicacaoAcordao() {
        return !this.marcacaoManualPublicacaoAcordao;
    }

    get tooltipMarcacaoPublicacaoAcordao() {
        if (this.marcacaoAutomaticaPublicacaoAcordao) {
            let title;

            const terminologiaDecisao =
                this.registro.processo.origem === Configuracao.ORIGEM_PJE ? 'dos movimentos' : 'do código de decisão';
            title = `Marcação inferida automaticamente pelo sistema, a partir do resultado de apreciação e ${terminologiaDecisao}`;
            title += this.registro.processo.origem === Configuracao.ORIGEM_PJE ? ' registrados.' : ' registrado.';
            title += '\nATENÇÃO: Ignore eventual marcação automática "Sim" para processo não apreciado com ';
            title += this.registro.processo.origem === Configuracao.ORIGEM_PJE ? 'movimento' : 'código de decisão';
            title += ' referente a resultado NÃO JULGADO.';

            return (
                <Tooltip
                    title={<Typography sx={{ whiteSpace: 'pre-line' }}>{title}</Typography>}
                    placement="top"
                >
                    <Warning style={{ fill: 'darkorange', marginRight: '2px' }} />
                </Tooltip>
            );
        } else if (this.isProcessoApreciado && !this.isProcessoJulgado && this.acordaoObrigatorio) {
            const title =
                'Marcação realizada manualmente, porém, não deveria ser publicado acórdão para processo não julgado! Avalie possível inconformidade.';

            return (
                <Tooltip
                    title={<Typography>{title}</Typography>}
                    placement="top"
                >
                    <KeyframesBlinkPublicacaoObrigatoriaInconsistente>
                        <Warning style={{ fill: 'darkorange', marginRight: '2px' }} />
                    </KeyframesBlinkPublicacaoObrigatoriaInconsistente>
                </Tooltip>
            );
        } else {
            const title = 'Marcação realizada manualmente.'; // [TODO] Marcação realizada por [usuário U] em DD-MM-AAAA às HH:MM:ss;

            return (
                <Tooltip
                    title={<Typography>{title}</Typography>}
                    placement="top"
                >
                    <Info style={{ fill: 'darkblue', marginRight: '2px' }} />
                </Tooltip>
            );
        }
    }

    get acordaoObrigatorio() {
        return SituacaoAcordaoBusiness.isPublicacaoAcordaoObrigatoria(this.registro);
    }

    handleClickAcordaoObrigatorio = async () => {
        let novoValor = !this.acordaoObrigatorio;
        await this.acordaoService.marcarAcordaoObrigatorio(
            this.registro.sessaoId,
            this.registro.processo.numeroCompleto,
            novoValor
        );
        runInAction(() => (this.registro.acordaoObrigatorio = novoValor));
        await this.sessaoStore.recarregarProcesso(this.registro);
    };

    render() {
        const TITLE_PADRAO = 'O acórdão deverá ser publicado nesta sessão?';
        let titleRadioGroup = TITLE_PADRAO;
        let desabilitarRadios = false;

        if (this.isProcessoApreciado && !this.isProcessoJulgado) {
            titleRadioGroup = 'Não é possível publicar acórdão para processos não julgados!';

            if (!this.acordaoObrigatorio) {
                desabilitarRadios = true;
            }
        } else if (this.registro.acordaoTransferido) {
            titleRadioGroup = 'Acórdão já enviado ao PJe!';
            desabilitarRadios = true;
        } else if (this.registro.movimentosTransferidos) {
            titleRadioGroup = 'Movimentos já enviados ao PJe!';
            desabilitarRadios = true;
        }

        return (
            <div style={{ display: 'flex', alignContent: 'center', alignItems: 'center' }}>
                <Typography style={{ color: '#01579b', fontWeight: 'bold', fontSize: '0.95rem' }}>
                    Publicação de acórdão?
                </Typography>
                <Tooltip
                    title={<Typography>{titleRadioGroup}</Typography>}
                    placement="bottom-start"
                >
                    <RadioGroup
                        value={this.acordaoObrigatorio}
                        onChange={this.handleClickAcordaoObrigatorio}
                        style={{ display: 'flex', flexDirection: 'row', flexGrow: 1, marginLeft: '10px' }}
                    >
                        <FormControlLabel
                            label="Sim"
                            value={true}
                            control={
                                <Radio
                                    disabled={desabilitarRadios}
                                    size="small"
                                />
                            }
                            style={{ margin: '0px' }}
                            sx={{ '& .MuiFormControlLabel-label': { marginLeft: '0px' } }}
                        />
                        <FormControlLabel
                            label="Não"
                            value={false}
                            control={
                                <Radio
                                    disabled={desabilitarRadios}
                                    size="small"
                                />
                            }
                            style={{ margin: '0px' }}
                            sx={{ '& .MuiFormControlLabel-label': { marginLeft: '0px' } }}
                        />
                    </RadioGroup>
                </Tooltip>
                <Box style={{ marginLeft: '20px' }}>{this.tooltipMarcacaoPublicacaoAcordao}</Box>
            </div>
        );
    }
}

export default inject('stores')(observer(PublicacaoAcordao));
